import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { RefreshCw, AlertCircle, CheckCircle2, Clock, XCircle, X, Copy } from 'lucide-react'
import type { ImportHistory } from '@/types/attendance-metrics'

/**
 * Extracts date portion (YYYY-MM-DD) from ISO string without timezone conversion.
 * This prevents the day shift issue when converting UTC dates to local timezone.
 */
function extractDatePart(isoString: string): string {
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
    return isoString
  }
  // Extract date portion from ISO string (before 'T')
  return isoString.split('T')[0]
}

/**
 * Formats a date string (YYYY-MM-DD or ISO) to pt-BR locale format (DD/MM/YYYY)
 * without timezone conversion issues.
 */
function formatDateBR(dateString: string): string {
  const datePart = extractDatePart(dateString)
  const [year, month, day] = datePart.split('-')
  return `${day}/${month}/${year}`
}

interface ImportHistoryListProps {
  imports: ImportHistory[]
  onRetry?: (startDate: string, endDate: string) => void
  onCancel?: (importId: string) => void
  onDuplicate?: (startDate: string, endDate: string) => void
  isLoading?: boolean
}

const statusConfig = {
  pending: {
    label: 'Pendente',
    variant: 'outline' as const,
    icon: Clock,
    color: 'text-yellow-500',
  },
  processing: {
    label: 'Processando',
    variant: 'default' as const,
    icon: RefreshCw,
    color: 'text-blue-500',
  },
  completed: {
    label: 'Concluído',
    variant: 'default' as const,
    icon: CheckCircle2,
    color: 'text-green-500',
  },
  failed: {
    label: 'Falhou',
    variant: 'destructive' as const,
    icon: XCircle,
    color: 'text-red-500',
  },
}

export function ImportHistoryList({ imports, onRetry, onCancel, onDuplicate, isLoading }: ImportHistoryListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    )
  }

  if (!imports.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center space-y-2">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
            <h3 className="font-semibold text-lg">Nenhuma importação</h3>
            <p className="text-sm text-muted-foreground">
              Inicie uma nova importação para ver o histórico aqui
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      {/* Mobile: Cards */}
      <div className="md:hidden space-y-3">
        {imports.map((item) => {
          const config = statusConfig[item.status]
          const Icon = config.icon

          return (
            <Card key={item.id}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`h-4 w-4 ${config.color}`} />
                      <Badge variant={config.variant}>{config.label}</Badge>
                    </div>
                    <p className="text-sm font-medium">
                      {formatDateBR(item.startDate)} até {formatDateBR(item.endDate)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(item.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Tickets</p>
                    <p className="font-semibold">
                      {item.ticketsProcessed} / {item.ticketsTotal}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Mensagens</p>
                    <p className="font-semibold">{item.messagesProcessed}</p>
                  </div>
                </div>

                {item.errorMessage && (
                  <div className="p-2 bg-destructive/10 rounded-md">
                    <p className="text-xs text-destructive">{item.errorMessage}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {onDuplicate && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => onDuplicate(extractDatePart(item.startDate), extractDatePart(item.endDate))}
                    >
                      <Copy className="h-3 w-3 mr-2" />
                      Duplicar
                    </Button>
                  )}
                  {item.status === 'failed' && onRetry && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => onRetry(extractDatePart(item.startDate), extractDatePart(item.endDate))}
                    >
                      <RefreshCw className="h-3 w-3 mr-2" />
                      Tentar novamente
                    </Button>
                  )}
                  {(item.status === 'pending' || item.status === 'processing') && onCancel && (
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => onCancel(item.id)}
                    >
                      <X className="h-3 w-3 mr-2" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Período</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Tickets</TableHead>
              <TableHead>Mensagens</TableHead>
              <TableHead>Data</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {imports.map((item) => {
              const config = statusConfig[item.status]
              const Icon = config.icon

              return (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    {formatDateBR(item.startDate)} até {formatDateBR(item.endDate)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${config.color}`} />
                      <Badge variant={config.variant}>{config.label}</Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.ticketsProcessed} / {item.ticketsTotal}
                  </TableCell>
                  <TableCell>{item.messagesProcessed}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(item.createdAt).toLocaleString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {onDuplicate && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Duplicar importação"
                          onClick={() => onDuplicate(extractDatePart(item.startDate), extractDatePart(item.endDate))}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      )}
                      {item.status === 'failed' && onRetry && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Tentar novamente"
                          onClick={() => onRetry(extractDatePart(item.startDate), extractDatePart(item.endDate))}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      )}
                      {(item.status === 'pending' || item.status === 'processing') && onCancel && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Cancelar"
                          onClick={() => onCancel(item.id)}
                        >
                          <X className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </>
  )
}
